import { Box, Flex, Stack, useDisclosure } from '@chakra-ui/react'
import { type CampaignEntity, type ComponentCampaignCurrentNft } from '@twlvxtwlv/cms'
import { type CurrentNftWithProduct } from '@twlvxtwlv/mapping'
import { type AuctionWithHighestBid } from '@twlvxtwlv/supabase'
import { type ArtistWithProducts, type Product } from '@twlvxtwlv/tokenforge'
import type {
  CampaignDetailsSlugType,
  CampaignNavigationRecord,
  CampaignNavigationRecords,
  NewsletterOptInInput,
  NotifyMeAttributes,
  NotifyMeOptInInput,
  RoadmapEntry,
} from '@twlvxtwlv/types'
import { useTranslation } from 'next-i18next'
import dynamic from 'next/dynamic'
import { NewsletterSuccessModal } from '../../../shared/modals/newsletter-success-modal'
import { NewsletterOptIn } from '../../../shared/newsletter-opt-in'
import { Section } from '../../../shared/section'
import { CampaignDetailsDesktop } from './campaign-details-desktop'
import { CampaignDetailsMobile } from './campaign-details-mobile'
import { SimilarProductsByGenre } from './similar-products-by-genre'

const NotifyMeModal = dynamic(
  () => import('../../../shared/modals/notify-me-modal').then((module) => module.NotifyMeModal),
  { ssr: false }
)

export type TitleLeftElementType = Pick<CampaignNavigationRecord, 'titleLeftElement'>

export interface SelectedNftWithProduct extends CurrentNftWithProduct, TitleLeftElementType {}

interface CampaignDetailsProps {
  artistsWithProducts: ArtistWithProducts[]
  auctionsWithHighestBid: AuctionWithHighestBid[]
  campaignContainsAward: boolean
  campaignEntity: CampaignEntity
  campaignNavigationRecords: CampaignNavigationRecords
  currentNftsWithProducts: CurrentNftWithProduct[]
  genre: string
  onAddNewsletterContact: (
    newsletterOptInInput: NewsletterOptInInput,
    onSuccess: () => void
  ) => void
  onNotifyMeAttributesChanged: (attributes: NotifyMeAttributes | null) => void
  onNotifyMeSubmit: (notifyMeOptInInput: NotifyMeOptInInput, onSuccess: () => void) => Promise<void>
  onSelectedNftWithProductChanged: (nft: SelectedNftWithProduct | null) => void
  onTrackAllNftsClicked: () => void
  onTrackLearnMoreButtonClicked: (
    currentNft: ComponentCampaignCurrentNft,
    priceInEuroCents: number
  ) => void
  onTrackNavigation: (slug: CampaignDetailsSlugType, product: Product | undefined | null) => void
  onTrackPurchaseButtonClicked: (
    currentNft: ComponentCampaignCurrentNft,
    priceInEuroCents: number
  ) => void
  roadmapEntries: RoadmapEntry[]
  similarProducts: Product[]
}

export function CampaignDetails({
  artistsWithProducts,
  auctionsWithHighestBid,
  campaignContainsAward,
  campaignEntity,
  campaignNavigationRecords,
  currentNftsWithProducts,
  genre,
  onAddNewsletterContact,
  onNotifyMeAttributesChanged,
  onNotifyMeSubmit,
  onSelectedNftWithProductChanged,
  onTrackAllNftsClicked,
  onTrackLearnMoreButtonClicked,
  onTrackNavigation,
  onTrackPurchaseButtonClicked,
  roadmapEntries,
  similarProducts,
}: CampaignDetailsProps) {
  const { t } = useTranslation(['common, campaign'])

  const {
    isOpen: isNewsletterSuccessModalOpen,
    onClose: onCloseNewsletterSuccessModal,
    onOpen: onOpenNewsletterSuccessModal,
  } = useDisclosure()

  const {
    isOpen: isNotifyMeModalOpen,
    onClose: onCloseNotifyMeModal,
    onOpen: onOpenNotifyMeModal,
  } = useDisclosure()

  function handleOnAddNewsletterContact(newsletterOptInInput: NewsletterOptInInput) {
    onAddNewsletterContact(newsletterOptInInput, () => {
      onOpenNewsletterSuccessModal()
    })
  }

  function handleOnNotifyMeSubmit(notifyMeOptInInput: NotifyMeOptInInput) {
    onNotifyMeSubmit(notifyMeOptInInput, () => {
      onOpenNewsletterSuccessModal()
      onCloseNotifyMeModal()
    })
  }

  return (
    <>
      <Section>
        <Flex w="full" h="full" gap={8} flexGrow={1}>
          <Box w="full" display={{ xl: 'none' }}>
            <CampaignDetailsMobile
              artistsWithProducts={artistsWithProducts}
              auctionsWithHighestBid={auctionsWithHighestBid}
              campaignEntity={campaignEntity}
              campaignNavigationRecords={campaignNavigationRecords}
              currentNftsWithProducts={currentNftsWithProducts}
              roadmapEntries={roadmapEntries}
              onTrackNavigation={onTrackNavigation}
              onTrackPurchaseButtonClicked={onTrackPurchaseButtonClicked}
              onTrackLearnMoreButtonClicked={onTrackLearnMoreButtonClicked}
              campaignContainsAward={campaignContainsAward}
              onOpenNotifyMeModal={onOpenNotifyMeModal}
              onNotifyMeAttributesChanged={onNotifyMeAttributesChanged}
            />
          </Box>
          <Box w="full" display={{ base: 'none', xl: 'flex' }}>
            <CampaignDetailsDesktop
              artistsWithProducts={artistsWithProducts}
              auctionsWithHighestBid={auctionsWithHighestBid}
              campaignContainsAward={campaignContainsAward}
              campaignEntity={campaignEntity}
              campaignNavigationRecords={campaignNavigationRecords}
              currentNftsWithProducts={currentNftsWithProducts}
              onNotifyMeAttributesChanged={onNotifyMeAttributesChanged}
              onOpenNotifyMeModal={onOpenNotifyMeModal}
              onSelectedNftWithProductChanged={onSelectedNftWithProductChanged}
              onTrackLearnMoreButtonClicked={onTrackLearnMoreButtonClicked}
              onTrackNavigation={onTrackNavigation}
              onTrackPurchaseButtonClicked={onTrackPurchaseButtonClicked}
              roadmapEntries={roadmapEntries}
            />
          </Box>
        </Flex>
      </Section>

      <Section maxWidth="full">
        <Stack bg="white" spacing={0}>
          {similarProducts && similarProducts.length > 0 ? (
            <SimilarProductsByGenre
              products={similarProducts}
              genre={genre}
              headline={campaignEntity.attributes?.containedNfts?.headline ?? undefined}
              subHeadline={campaignEntity.attributes?.containedNfts?.subHeadline ?? undefined}
              onTrackAllNftsClicked={onTrackAllNftsClicked}
            />
          ) : null}

          <NewsletterOptIn onSubmit={handleOnAddNewsletterContact} />
        </Stack>
      </Section>

      <NotifyMeModal
        isOpen={isNotifyMeModalOpen}
        onClose={onCloseNotifyMeModal}
        onNotifyMeSubmit={handleOnNotifyMeSubmit}
        onNotifyMeAttributesChanged={onNotifyMeAttributesChanged}
      />

      <NewsletterSuccessModal
        isOpen={isNewsletterSuccessModalOpen}
        onClose={onCloseNewsletterSuccessModal}
        headingText={t('common:newsletter-signup-modal-heading')}
        buttonText={t('common:newsletter-signup-modal-button')}
        bodyText={t('common:newsletter-signup-modal-text')}
      />
    </>
  )
}
